import { useSelector } from 'react-redux';

import type { IUndergroundMapApi } from '@cian/underground-map-widget';
import { EMetroTravelType } from '../../../../../../packages/api-models/common/json_query';
import { LoadableErrorBoundary } from '../../../../../../packages/LoadableHelpers';
import { fetchUndergroundMapData } from 'shared/services/fetchUndergroundMapData';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { useAppDispatch } from 'shared/hooks';
import { useRef, useCallback, FC } from 'react';
import {
  selectJsonQuery,
  selectTravelTime,
  selectTravelType,
  selectUndergroundStations,
} from 'shared/selectors/jsonQuery';
import { selectLocationIdForMetroMap } from 'shared/selectors/geo';
import { setJsonQuery } from 'shared/slices/jsonQuery';
import { updateSearchMeta } from 'shared/thunks/searchMeta';
import { IStationData } from 'shared/types/geo';
import { setUndergroundTravel } from '../../../../../../packages/JsonQuery/setUndergroundTravel';
import { unsetUndergroundTravel } from '../../../../../../packages/JsonQuery/unsetUndergroundTravel';
import { UndergroundMapModalHeader } from '../../../../../../packages/GeoComponents';
import { removeUndergroundStations, appendUndergroundStations } from 'shared/thunks/geo/undergrounds';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { getDefaultTravelTimeForType } from 'shared/utils/underground';
import { GeoSuggestModalContent } from '../../../../../components/GeoSuggestModalContent';
import { UndergroundMapContainerLoadable } from '../MapModalContainer/UndergroundMapContainer';
import { UndergroundMapLineSelector } from '../MapModalContainer/UndergroundMapLineSelector';
import { trackMetroModalOpenEvent } from '../MapModalContainer/tracking';

export const UndergroundMapModalContainerV2: FC = () => {
  const ctx = useApplicationContext();
  const dispatch = useAppDispatch();
  const undergroundMapApiRef = useRef<IUndergroundMapApi>(null);
  const selectedStations = useSelector(selectUndergroundStations);
  const locationId = useSelector(selectLocationIdForMetroMap);
  const travelTime = useSelector(selectTravelTime);
  const travelType = useSelector(selectTravelType) || EMetroTravelType.Any;
  const jsonQuery = useSelector(selectJsonQuery);

  const handleRemove = useCallback(
    (stations: IStationData[]) => {
      dispatch(removeUndergroundStations(stations));
    },
    [dispatch],
  );

  const handleSelect = useCallback(
    (stations: IStationData[]) => {
      dispatch(appendUndergroundStations(stations));
    },
    [dispatch],
  );

  const handleTravelInfoChange = (newTravelType: EMetroTravelType, newTravelTime?: number) => {
    let newJsonQuery;
    if (travelType !== EMetroTravelType.Any && newTravelType === EMetroTravelType.Any) {
      // Переключили на тип "неважно". Сбрасываем все фильтры
      newJsonQuery = unsetUndergroundTravel(jsonQuery)();
    } else if (travelType === EMetroTravelType.Any && newTravelType !== EMetroTravelType.Any) {
      // Переключили на тип, отличный от "неважно". Устанавливаем фильтр времени в дефолт
      newJsonQuery = setUndergroundTravel(jsonQuery)(newTravelType, getDefaultTravelTimeForType(newTravelType));
    } else {
      // В остальных случаях применяем выбранные фильтры, как есть.
      // Если время не указано, то его не будет в запросе и фильтр будет работать аналогично типу "неважно"
      newJsonQuery = setUndergroundTravel(jsonQuery)(newTravelType, newTravelTime);
    }

    dispatch(setJsonQuery(newJsonQuery));
    dispatch(updateSearchMeta());
  };

  const handleFetchData = useCallback((id: number) => fetchUndergroundMapData(id, ctx), [ctx]);

  /* istanbul ignore next */
  const handleRetry = () => UndergroundMapContainerLoadable.load();

  return (
    <ActionAfterViewObserver callback={trackMetroModalOpenEvent} triggerOnce>
      <>
        <UndergroundMapModalHeader
          isRedesign
          fastSelectionButtons={<UndergroundMapLineSelector api={undergroundMapApiRef} />}
          travelType={travelType}
          travelTime={travelTime}
          onUndergroundTravelChanged={handleTravelInfoChange}
        />
        <LoadableErrorBoundary logger={ctx.logger} onRetry={handleRetry}>
          <GeoSuggestModalContent>
            <UndergroundMapContainerLoadable
              apiRef={undergroundMapApiRef}
              locationId={locationId}
              onDataRequested={handleFetchData}
              onRemove={handleRemove}
              onSelect={handleSelect}
              selectedStations={selectedStations}
            />
          </GeoSuggestModalContent>
        </LoadableErrorBoundary>
      </>
    </ActionAfterViewObserver>
  );
};
